import { DateTime } from "luxon";

/** Which date of the range a click sets */
export enum RangeEndpoint {
  start = "start",
  end = "end",
}

/** The calendar unit a clicked cell spans */
export type CellUnit = "day" | "month";

/**
 * As in MUI's range pickers: a click sets the start (ending the range on the same cell) and
 * the next click sets the end. An end picked before the start begins the range there instead.
 *
 * @returns The new range, and the endpoint the following click sets
 * @param date The clicked cell
 * @param unit The unit the cell spans
 * @param endpoint The endpoint this click sets
 * @param start Inclusive start of the active range
 */
export function pickDate(
  date: DateTime,
  unit: CellUnit,
  endpoint: RangeEndpoint,
  start?: Date,
): { range: [start: Date, end: Date]; next?: RangeEndpoint } {
  const from = date.startOf(unit).toJSDate();
  const to = date.endOf(unit).toJSDate();
  if (endpoint === RangeEndpoint.end && start && from >= start) {
    return { range: [start, to] };
  }
  return { range: [from, to], next: RangeEndpoint.end };
}

/**
 * @returns The range with one endpoint changed, or undefined if it would cross the other
 * @param date The clicked cell
 * @param unit The unit the cell spans
 * @param endpoint The endpoint explicitly chosen in the heading
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function editDate(
  date: DateTime,
  unit: CellUnit,
  endpoint: RangeEndpoint,
  start?: Date,
  end?: Date,
): [start: Date, end: Date] | undefined {
  const from = date.startOf(unit).toJSDate();
  const to = date.endOf(unit).toJSDate();
  if (endpoint === RangeEndpoint.start) {
    if (end && from > end) return;
    return [from, end ?? to];
  }
  if (start && to < start) return;
  return [start ?? from, to];
}
